import { api } from './api';

export type BillingTier = 'STARTER' | 'PRO' | 'BUSINESS' | 'ENTERPRISE';

interface BillingCheckoutResult {
  url?: string | null;
  sessionId?: string | null;
  tierApplied?: BillingTier | null;
}

/** What customers read when checkout cannot start because billing is not set up on the server. */
export const CHECKOUT_UNAVAILABLE_MESSAGE = "Checkout is temporarily unavailable. Email support@responsedock.com and we'll activate your plan today.";

export const SUPPORT_EMAIL = 'support@responsedock.com';
export const SALES_EMAIL = 'sales@responsedock.com';

export class BillingConfigurationError extends Error {
  technicalMessage: string;
  /** Operator-facing explanation (environment variables, price IDs): shown to system admins only. */
  operatorMessage: string;

  constructor(operatorMessage: string, technicalMessage: string) {
    super(CHECKOUT_UNAVAILABLE_MESSAGE);
    this.name = 'BillingConfigurationError';
    this.operatorMessage = operatorMessage;
    this.technicalMessage = technicalMessage;
  }
}

/** Customer copy for a failed checkout, plus operator detail only for system admins. */
export function checkoutErrorCopy(error: unknown, isSystemAdmin = false): { message: string; operatorDetail: string | null } {
  if (error instanceof BillingConfigurationError) {
    return {
      message: CHECKOUT_UNAVAILABLE_MESSAGE,
      operatorDetail: isSystemAdmin ? `${error.operatorMessage} (${error.technicalMessage})` : null,
    };
  }
  const message = error instanceof Error && error.message ? error.message : 'Unable to start checkout.';
  return { message, operatorDetail: null };
}

const PENDING_BILLING_TIER_KEY = 'ResponseDock-pending-billing-tier';

export function billingTierFromPlanParam(plan: string | null | undefined): BillingTier | null {
  const normalized = plan?.trim().toLowerCase();
  if (normalized === 'starter') return 'STARTER';
  if (normalized === 'pro') return 'PRO';
  if (normalized === 'business') return 'BUSINESS';
  if (normalized === 'enterprise') return 'ENTERPRISE';
  return null;
}

/** Free-trial signup links carry `trial=1` (also accepts `true`/`yes`). */
export function isTrialSignup(search: string | URLSearchParams): boolean {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const value = params.get('trial')?.trim().toLowerCase();
  return value === '1' || value === 'true' || value === 'yes';
}

/**
 * The paid tier a signup/login URL asks for. A trial signup never asks for one, even when an
 * older link also carries `plan=pro`: trials start on the free plan and never open checkout.
 */
export function requestedBillingTier(search: string | URLSearchParams): BillingTier | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  if (isTrialSignup(params)) return null;
  return billingTierFromPlanParam(params.get('plan'));
}

export function isCheckoutBillingTier(tier: BillingTier | null | undefined): tier is 'PRO' | 'BUSINESS' {
  return tier === 'PRO' || tier === 'BUSINESS';
}

export function storePendingBillingTier(tier: BillingTier | null | undefined) {
  if (typeof window === 'undefined') return;
  if (!isCheckoutBillingTier(tier)) {
    window.localStorage.removeItem(PENDING_BILLING_TIER_KEY);
    return;
  }
  window.localStorage.setItem(PENDING_BILLING_TIER_KEY, tier);
}

export function getPendingBillingTier(): BillingTier | null {
  if (typeof window === 'undefined') return null;
  return billingTierFromPlanParam(window.localStorage.getItem(PENDING_BILLING_TIER_KEY));
}

export function clearPendingBillingTier() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(PENDING_BILLING_TIER_KEY);
}

function billingError(error: unknown, fallback: string): Error {
  const apiError = error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
    message?: string;
  };
  const serverMessage = apiError.response?.data?.error ?? apiError.response?.data?.message;

  if (apiError.response?.status === 503 && serverMessage === 'Stripe is not configured.') {
    return new BillingConfigurationError(
      'Stripe checkout is not configured on the API server yet.',
      serverMessage,
    );
  }

  if (apiError.response?.status === 503 && serverMessage?.startsWith('Missing Stripe price ID')) {
    return new BillingConfigurationError(
      'This plan is missing its Stripe price configuration.',
      serverMessage,
    );
  }

  return new Error(serverMessage ?? apiError.message ?? fallback);
}

export async function startBillingCheckout(workspaceId: string, tier: BillingTier): Promise<BillingCheckoutResult> {
  const res = await api.post<BillingCheckoutResult>(`/workspaces/${workspaceId}/billing/checkout`, { tier }).catch((error: unknown) => {
    throw billingError(error, 'Unable to start Stripe checkout.');
  });
  const url = res.data.url;
  if (!url && !res.data.tierApplied) {
    throw new Error('Stripe checkout URL was not returned.');
  }
  if (url) {
    window.location.href = url;
  }
  return res.data;
}

export async function confirmBillingCheckout(workspaceId: string, sessionId: string) {
  const res = await api.post<{ tierApplied: BillingTier | null }>(`/workspaces/${workspaceId}/billing/confirm`, { sessionId }).catch((error: unknown) => {
    throw billingError(error, 'Unable to confirm Stripe checkout.');
  });
  return res.data;
}

export async function openBillingPortal(workspaceId: string) {
  const res = await api.post<{ url?: string }>(`/workspaces/${workspaceId}/billing/portal`, {}).catch((error: unknown) => {
    throw billingError(error, 'Unable to open Stripe billing portal.');
  });
  const url = res.data.url;
  if (!url) {
    throw new Error('Stripe billing portal URL was not returned.');
  }
  window.location.href = url;
}
